import api from '@/api/axiosApi'
import {
  PlanDetailResponse,
  UpdatePlanItemPayload,
  PlanDetailItem,
  ApiPlanDetailResponse,
  BulkOperationResponse,
} from '@/types'
import { buildHierarchyBreadcrumb } from '@/utils/formatters'

// Backend schema types
interface LineItemRead {
  // Identity
  id: string
  version_id: string
  line_number: number
  sort_key: string

  // Product info
  sku_number: string | null
  upc: string | null
  vendor_style: string | null
  description: string | null

  // Hierarchy
  division: string | null
  department: string | null
  class: string | null
  subclass: string | null

  // Vendor
  vendor_id: string | null
  vendor_code?: string | null
  vendor_name: string | null

  // Financial fields (stored as strings for precision)
  target_retail: string | null
  target_cost: string | null
  target_margin: string | null
  ticket_retail: string | null
  current_cost: string | null
  quoted_cost: string | null
  final_cost: string | null

  // Plan units/dollars
  ty_plan_units: number | null
  ty_plan_dollars: string | null
  ty_plan_cost: string | null

  // Last year data
  ly_sales_units: number | null
  ly_sales_dollars: string | null
  ly_sales_cost: string | null
  ly_sell_through: string | null
  ly_gross_margin: string | null

  // Logistics
  moq: number | null
  pack_size: number | null
  case_pack: number | null
  inner_pack: number | null
  lead_time_days: number | null

  // Store groups
  active_store_group_ids: string[]

  // References
  reference_line_id: string | null
  store_model_id: string | null
  sourcing_option_id: string | null
  adopted_from_option_at: string | null

  // Workflow
  workflow_status: 'IN_PROGRESS' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  workflow_status_changed_at: string | null
  workflow_status_changed_by: string | null

  // Metadata
  row_version: number
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
  custom_attributes: Record<string, unknown> | null
}

interface PaginatedVersionLinesResponse {
  data: LineItemRead[]
  pagination: {
    skip: number
    limit: number
    total: number
    has_more: boolean
  }
}

interface BulkCopyResponse {
  copied: LineItemRead[]
}

/**
 * Maps backend LineItemRead to frontend PlanDetailItem
 */
function mapVersionLineToDetailItem(line: LineItemRead): PlanDetailItem {
  // Build composite SKU identifier from hierarchy fields
  const hierarchyParts = [
    line.division,
    line.department,
    line.class,
    line.subclass,
  ].filter(Boolean)
  const skuIdentifier = hierarchyParts.join('-') || ''

  return {
    id: line.id,
    skuIdentifier,
    skuNumber: line.sku_number || '',
    description: line.description || '',
    status: line.workflow_status,
    // Hierarchy fields
    division: line.division,
    department: line.department,
    class_: line.class,
    subclass: line.subclass,
    // Vendor
    vendorName: line.vendor_name,
    // Financial fields
    targetRetail: line.target_retail,
    targetCost: line.target_cost,
    targetMargin: line.target_margin,
    landedCost: null,
    // Plan units
    tyPlanUnits: line.ty_plan_units,
    // Last year data
    lySalesUnits: line.ly_sales_units,
    lySellThrough: line.ly_sell_through,
    // Store model
    storeModelId: line.store_model_id,
    // Store groups
    activeStoreGroupIds: line.active_store_group_ids ?? [],
    // Variant count (placeholder - backend not ready)
    variantCount: 1,
  }
}

/**
 * Fetches plan details from backend API
 */
async function fetchPlanDetailFromApi(
  planId: string
): Promise<ApiPlanDetailResponse> {
  const response = await api.get<ApiPlanDetailResponse>(`/plans/${planId}`)
  return response.data
}

/**
 * Main fetch function with transformation
 */
export async function fetchPlanDetail(
  planId: string,
  versionId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<PlanDetailResponse> {
  // Fetch plan metadata
  const apiPlan = await fetchPlanDetailFromApi(planId)

  // Calculate offset for backend pagination
  const offset = (page - 1) * pageSize

  // Fetch lines from the specified version
  const linesResponse = await api.get<PaginatedVersionLinesResponse>(
    `/plans/${planId}/versions/${versionId}/lines`,
    {
      params: {
        offset,
        limit: pageSize,
        sort: '-created_at',
      },
    }
  )

  // Map backend lines to frontend items
  const items = linesResponse.data.data.map(mapVersionLineToDetailItem)

  // Calculate total pages
  const totalCount = linesResponse.data.pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    id: apiPlan.id,
    name: apiPlan.name,
    status: apiPlan.status ?? 'DRAFT',
    hierarchy: buildHierarchyBreadcrumb(apiPlan.hierarchy),
    items,
    pagination: {
      page,
      pageSize,
      totalCount,
      totalPages,
    },
  }
}

/**
 * Adds a new line to the plan, optionally prepopulated with hierarchy data
 */
export async function addPlanLine(
  planId: string,
  versionId: string,
  initialData?: {
    division?: string | null
    department?: string | null
    class?: string | null
    subclass?: string | null
  }
): Promise<PlanDetailItem> {
  const body: Record<string, string> = {}
  if (initialData?.division) body.division = initialData.division
  if (initialData?.department) body.department = initialData.department
  if (initialData?.class) body.class = initialData.class
  if (initialData?.subclass) body.subclass = initialData.subclass

  const response = await api.post<LineItemRead>(
    `/plans/${planId}/versions/${versionId}/lines`,
    body
  )
  return mapVersionLineToDetailItem(response.data)
}

/**
 * Updates a plan item with new values
 */
export async function updatePlanItem(
  planId: string,
  versionId: string,
  itemId: string,
  updates: UpdatePlanItemPayload
): Promise<PlanDetailItem> {
  // Map frontend field names to backend field names
  const backendUpdates: Record<string, string> = {}

  const FIELD_MAP: Record<string, string | null> = {
    skuNumber: 'sku_number',
    description: 'description',
    status: 'workflow_status',
    // Mappings for SKU auto-fill
    vendorId: 'vendor_id',
    vendorName: 'vendor_name',
    department: 'department',
    class_: 'class',
    targetRetail: 'target_retail',
    targetCost: 'target_cost',
  }

  for (const [key, value] of Object.entries(updates)) {
    const backendField = FIELD_MAP[key]
    if (backendField) {
      // Status values are identical between frontend and backend
      backendUpdates[backendField] = value
    }
  }

  const response = await api.patch<LineItemRead>(
    `/plans/${planId}/versions/${versionId}/lines/${itemId}`,
    backendUpdates
  )
  return mapVersionLineToDetailItem(response.data)
}

/**
 * Copies a plan item (creates duplicate)
 */
export async function copyPlanItem(
  planId: string,
  versionId: string,
  itemId: string
): Promise<PlanDetailItem> {
  // API uses bulk copy endpoint - pass single line_id in array
  const response = await api.post<BulkCopyResponse>(
    `/plans/${planId}/versions/${versionId}/lines/copy`,
    {
      line_ids: [itemId],
      target_version_id: null, // Copy within same version
    }
  )

  // Return the first (and only) copied line
  const copiedLine = response.data.copied[0]
  return mapVersionLineToDetailItem(copiedLine)
}

/**
 * Deletes a plan item
 */
export async function deletePlanItem(
  planId: string,
  versionId: string,
  itemId: string
): Promise<{ success: boolean }> {
  await api.delete(`/plans/${planId}/versions/${versionId}/lines/${itemId}`)
  return { success: true }
}

/**
 * Submits plan items for approval
 */
export async function submitPlanItems(
  planId: string,
  versionId: string,
  itemIds: string[]
): Promise<BulkOperationResponse> {
  const response = await api.post<BulkOperationResponse>(
    `/plans/${planId}/versions/${versionId}/lines/submit`,
    { line_ids: itemIds }
  )
  return response.data
}

// Bulk import types
export interface ImportPreview {
  valid_rows: number
  matching_rows: number
  new_rows: number
  affected_lines: number
  issues: {
    row_number: number
    column: string
    message: string
    line_item_id: string | null
  }[]
  /** File rows that update an existing item in the version. */
  matched_row_numbers?: number[]
  /** Value mismatches that don't block because the row keeps the item's
   * current value (e.g. a re-imported export). */
  unchanged_mismatches?: ImportPreview['issues']
}

export async function previewPlanImport(
  planId: string,
  versionId: string,
  productListId: string,
  upsert: boolean | null,
  signal?: AbortSignal
): Promise<ImportPreview> {
  const response = await api.post<ImportPreview>(
    `/plans/${planId}/versions/${versionId}/lines/import-preview`,
    { product_list_id: productListId, upsert },
    { signal }
  )
  return response.data
}

export interface BulkAddFromProductListResponse {
  processing_id: string
  resource_id: string
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
  events_url: string
}

/**
 * Bulk add line items from a product list to a specific plan version.
 * With upsert, update every matching SKU in that version and add unmatched SKUs.
 * Returns 202 with a processing handle and SSE events_url.
 */
export async function bulkAddFromProductList(
  planId: string,
  versionId: string,
  productListId: string,
  upsert = false
): Promise<BulkAddFromProductListResponse> {
  const response = await api.post<BulkAddFromProductListResponse>(
    `/plans/${planId}/versions/${versionId}/lines/bulk-add`,
    { product_list_id: productListId, upsert }
  )
  return response.data
}

/**
 * Creates a new version for a plan
 */
export async function createPlanVersion(
  planId: string,
  payload: {
    source_version_id: string
    include_lines?: boolean
    alias?: string
    notes?: string | null
  }
): Promise<{ id: string; is_active: boolean }> {
  const response = await api.post(`/plans/${planId}/versions`, payload)
  return response.data
}

// --- Version list types & fetch ---

export interface VersionSummary {
  id: string
  version_number: number
  alias: string
  is_active: boolean
  line_count: number
  notes: string | null
  created_at: string
}

export async function fetchPlanVersions(
  planId: string
): Promise<VersionSummary[]> {
  const response = await api.get<VersionSummary[]>(`/plans/${planId}/versions`)
  return response.data
}

/**
 * Activates a specific version of a plan
 */
export async function activateVersion(planId: string, versionId: string) {
  const response = await api.post(
    `/plans/${planId}/versions/${versionId}/activate`
  )
  return response.data
}

/**
 * Deletes a specific version of a plan
 */
export async function deleteVersion(planId: string, versionId: string) {
  await api.delete(`/plans/${planId}/versions/${versionId}`)
  return { success: true }
}

/**
 * Updates a specific version of a plan (e.g. rename alias)
 */
export async function updatePlanVersion(
  planId: string,
  versionId: string,
  payload: { alias?: string | null; notes?: string | null }
): Promise<VersionSummary> {
  const response = await api.patch<VersionSummary>(
    `/plans/${planId}/versions/${versionId}`,
    payload
  )
  return response.data
}

/**
 * Move lines to a different version (possibly in another plan)
 */
export async function moveLinesToVersion(
  planId: string,
  versionId: string,
  lineIds: string[],
  targetVersionId: string
): Promise<{ moved: LineItemRead[] }> {
  const response = await api.post<{ moved: LineItemRead[] }>(
    `/plans/${planId}/versions/${versionId}/lines/move`,
    { line_ids: lineIds, target_version_id: targetVersionId }
  )
  return response.data
}

/**
 * Bulk delete plan lines
 */
export async function bulkDeletePlanLines(
  planId: string,
  versionId: string,
  lineIds: string[]
): Promise<BulkOperationResponse> {
  const response = await api.post<BulkOperationResponse>(
    `/plans/${planId}/versions/${versionId}/lines/bulk-delete`,
    { line_ids: lineIds }
  )
  return response.data
}
